import { useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useLocale } from '@hooks/useLocale';
import Cookies from 'js-cookie';
import { useAddItem } from '@adapters/bigcommerce/hooks/cart';
import {
  ALGONOMY_TOP_CATEGORY_EN,
  ALGONOMY_TOP_CATEGORY_FR,
} from '@adapters/algonomy/constants';
import Button, { ButtonProps } from '@components/ui/Button';
import IconWrapper from '@components/ui/Icons';
import LoadingSpinner from '@components/ui/LoadingSpinner';
import { useUI } from '@context/GlobalUIContext';
import { usePdp } from '@context/PdpContext';
import { useCartUI } from '@context/CartContext';
import { useStoresUI } from '@context/StoresContext';
import { useGiftCard } from '@context/GiftCardContext';
import { dataLayerSaveForLaterAddToCart } from '@utils/pdpDataLayer';
import { triggerAnalytics } from '@utils/analytics/triggerAnalytics';
import {
  AvailabilityStatus,
  InventoryStatus,
  PriceType,
} from '@adapters/bigcommerce/types/product';
import {
  SPU_COOKIE,
  STS_COOKIE,
  SPU_CHANGED_MARKER,
} from '@config/publicConstants';
import { VariantDetailsData } from '@context/SaveForLaterContext/types';
import cn from 'classnames';
import styles from './ProductAddToCartButton.module.scss';
import { useSaveForLater } from '@context/SaveForLaterContext';
import { CART_COLOUR, CART_SIZE } from '@adapters/bigcommerce/constants';
import { trackAlgoliaAddToCart } from '@utils/algolia/analytics';
import { trackAddToCartEvent, GA4_EVENT_NAMES } from '@utils/gtm/ga4';
import { getGenderFromString } from '@utils/gtm/ga4/utils';
import { albyTrackAddToCart } from '@utils/alby';
import {
  FEATURE_FLAG_ALBY_CHATBOT,
  FEATURE_FLAG_MARKETPLACE,
  marketplacerFFCookieName,
} from '@config/feature-flag';

const { ADD_TO_CART, MINI_CART } = GA4_EVENT_NAMES;

const ProductAddToCartButton = ({
  changeDeliveryMethodToAddress,
  saveForLaterItemToAddToCart,
}: {
  changeDeliveryMethodToAddress?: boolean;
  saveForLaterItemToAddToCart?: VariantDetailsData;
}) => {
  const { t } = useTranslation(['pdp', 'common']);
  const pdpData = usePdp();
  const { addToCartClicked, setAddToCartClicked } = pdpData;
  const locale = useLocale();
  const newLocale = locale === 'fr' ? 'fr' : 'en';
  const isMarketplaceEnabled =
    FEATURE_FLAG_MARKETPLACE ||
    Cookies.get(marketplacerFFCookieName) === 'true';
  const isSaveForLater = !!saveForLaterItemToAddToCart;

  const {
    quantity,
    entityId,
    brand,
    gaName,
    erpCategory,
    selectedVariant,
    selectedAvailability,
    categories,
    colourOptionId,
    sizeOptionId,
    isGiftCard,
    sizes,
    price,
    productCode,
    breadcrumbs,
    availabilityStatus,
    description,
    isCanadianBrand,
    madeInCanada,
    hasComparison,
    hasMemberPriceDifference,
    promoBadge,
    productTags,
  } = isSaveForLater ? saveForLaterItemToAddToCart : pdpData;

  const selectedColour = {
    id: isSaveForLater
      ? saveForLaterItemToAddToCart?.selectedColour?.id
      : pdpData?.selectedColour?.id,
    name: isSaveForLater
      ? saveForLaterItemToAddToCart?.selectedColour?.name?.[newLocale]
      : pdpData?.selectedColour?.name,
  };

  const selectedSize = {
    id: isSaveForLater
      ? saveForLaterItemToAddToCart?.selectedSize?.id
      : pdpData?.selectedSize?.id,
    name: isSaveForLater
      ? saveForLaterItemToAddToCart?.selectedSize?.name?.[newLocale]
      : pdpData?.selectedSize?.name,
  };

  const { openCartFlyout } = useUI();
  const {
    deliveryOption,
    totalQuantity,
    setCartIsUpdating,
    setCartDoneUpdating,
    validateCartItems,
  } = useCartUI();

  const { favouriteStoreDetails } = useStoresUI();
  const { removeSaveForLaterItem, onAddToCart } = useSaveForLater();

  const [addingToCart, setAddingToCart] = useState(false);

  const topLevelCategoryId =
    locale === 'fr' ? ALGONOMY_TOP_CATEGORY_FR : ALGONOMY_TOP_CATEGORY_EN;

  const {
    giftCardRecipientsName,
    giftCardSendersName,
    giftCardRecipientsEmail,
    giftCardDeliveryDate,
    giftCardMessage,
    giftCardRecipientsNameOptionId,
    giftCardSendersNameOptionId,
    giftCardRecipientsEmailOptionId,
    giftCardDeliveryDateOptionId,
    giftCardMessageOptionId,
    isValidEmail,
    minDeliveryDate,
    maxDeliveryDate,
  } = useGiftCard();

  const addItem = useAddItem((x) =>
    triggerAnalytics({
      ...x,
      categories,
      categoryId: pdpData?.IS_ALGONOMY_PATH_CAT_ID_ENABLED
        ? `/${locale}${
            breadcrumbs?.[breadcrumbs.length - 1]?.path ?? locale === 'fr'
              ? `/fr/produits/`
              : `/en/products/`
          }`
        : breadcrumbs?.[breadcrumbs.length - 1]?.entityId.toString() ??
          topLevelCategoryId,
    })
  );

  const deliveryDateIsLessThanMinDeliveryDate =
    minDeliveryDate && giftCardDeliveryDate < minDeliveryDate;
  const deliveryDateIsMoreThanMaxDeliveryDate =
    maxDeliveryDate && giftCardDeliveryDate > maxDeliveryDate;

  let buttonVariant: ButtonProps['variant'] = 'primary';
  if (changeDeliveryMethodToAddress) {
    buttonVariant = 'tertiary';
  }
  if (isSaveForLater) {
    buttonVariant = 'unstyled';
  }

  if (!entityId) return null;

  const onAddToCartClick = async () => {
    if (!addToCartClicked) setAddToCartClicked(true);
    if (isSaveForLater) setCartIsUpdating();

    if (
      !selectedVariant?.entityId ||
      !quantity ||
      !colourOptionId ||
      (sizes?.length && !sizeOptionId) ||
      (!isGiftCard && !selectedAvailability)
    ) {
      return;
    }

    if (
      isGiftCard &&
      (!giftCardRecipientsEmail ||
        !isValidEmail ||
        !giftCardDeliveryDate ||
        deliveryDateIsLessThanMinDeliveryDate ||
        deliveryDateIsMoreThanMaxDeliveryDate)
    ) {
      return;
    }

    if (deliveryOption !== 'shipping' && !favouriteStoreDetails) {
      console.error(
        'Error adding to cart. User must select a store pickup location.'
      );
      return;
    }

    const storeCookie = Cookies.get(SPU_COOKIE);

    let storeName;
    if (storeCookie) {
      try {
        storeName = JSON.parse(storeCookie).storeName.replace(
          SPU_CHANGED_MARKER,
          ''
        );
      } catch (error) {
        console.error(
          `Error parsing SPU_COOKIE in ProductAddToCartButton: ${error}`
        );
      }
    }

    if (deliveryOption !== 'shipping') {
      const hasSelectedVariant = !!selectedVariant;

      const favouriteStoreId = favouriteStoreDetails?.data?.mec_store_id;
      const hasSetFavouriteStore =
        !!favouriteStoreDetails && typeof favouriteStoreId !== 'undefined';

      const storeInStock =
        hasSetFavouriteStore &&
        hasSelectedVariant &&
        !!selectedVariant?.storeInventory[favouriteStoreId]?.status &&
        !(
          selectedVariant?.storeInventory[favouriteStoreId]?.status ===
            InventoryStatus.OUT_OF_STOCK ||
          selectedVariant?.storeInventory[favouriteStoreId]?.status ===
            InventoryStatus.NOT_STOCKED
        );

      const stsDCInStock =
        hasSelectedVariant &&
        !!selectedVariant?.storeInventory[99]?.status &&
        !(
          selectedVariant?.storeInventory[99]?.status ===
            InventoryStatus.OUT_OF_STOCK ||
          selectedVariant?.storeInventory[99]?.status ===
            InventoryStatus.NOT_STOCKED
        );

      const stsShopInStock = !!selectedVariant?.hasStockAvailable;

      const stsInStock = stsDCInStock || stsShopInStock;

      if (storeName) {
        // Set Ship to store cookie
        try {
          const stsCookie = JSON.parse(Cookies.get(STS_COOKIE) || 'false');
          Cookies.set(
            STS_COOKIE,
            String(stsCookie || (!storeInStock && stsInStock)),
            {
              expires: 30,
            }
          );
        } catch (err) {
          console.log(err);
        }

        let storeNameSPU = favouriteStoreDetails.data.store_name;
        if (storeName !== deliveryOption) {
          storeNameSPU = `${favouriteStoreDetails.data.store_name}${SPU_CHANGED_MARKER}`;
          setTimeout(() => validateCartItems(true), 0); // Trigger validation after setting the cookie below
        }

        if (!isMarketplaceEnabled) {
          // Set Store pick up cookie
          Cookies.set(
            SPU_COOKIE,
            {
              storeName: storeNameSPU,
              storeId: favouriteStoreDetails.data.mec_store_id,
            },
            { expires: 30 }
          );
        }
      } else {
        // Set Ship to store cookie
        if (!isMarketplaceEnabled) {
          try {
            const stsCookie = JSON.parse(Cookies.get(STS_COOKIE) || 'false');
            Cookies.set(
              STS_COOKIE,
              String(stsCookie || (!storeInStock && stsInStock)),
              {
                expires: 30,
              }
            );
          } catch (err) {
            console.log(err);
          }
        }

        let storeNameSPU = favouriteStoreDetails.data.store_name;
        if (totalQuantity > 0) {
          storeNameSPU = `${favouriteStoreDetails.data.store_name}${SPU_CHANGED_MARKER}`;
        }
        // Set Store pick up cookie
        if (!isMarketplaceEnabled) {
          Cookies.set(
            SPU_COOKIE,
            {
              storeName: storeNameSPU,
              storeId: favouriteStoreDetails.data.mec_store_id,
            },
            { expires: 30 }
          );
        }
      }
    } else {
      if (!isMarketplaceEnabled) {
        Cookies.remove(SPU_COOKIE);
        Cookies.remove(STS_COOKIE);
      }
    }
    setAddingToCart(true);
    if (isSaveForLater) setCartIsUpdating();

    try {
      await addItem({
        productName: gaName,
        productId: entityId,
        variantId: selectedVariant.entityId,
        quantity,
        optionSelections: isGiftCard
          ? [
              {
                option_id: colourOptionId,
                option_value: `${selectedColour?.id}`,
                name: 'cart.giftcard.card.design',
                value: `${selectedColour?.name}`,
              },
              ...(sizeOptionId
                ? [
                    {
                      option_id: sizeOptionId,
                      option_value: `${selectedSize?.id}`,
                      name: CART_SIZE,
                      value: `${selectedSize?.name}`,
                    },
                  ]
                : []),
              {
                option_id: giftCardRecipientsNameOptionId,
                option_value: giftCardRecipientsName,
                name: 'cart.giftcard.recipients.name',
                value: giftCardRecipientsName,
              },
              {
                option_id: giftCardSendersNameOptionId,
                option_value: giftCardSendersName,
                name: 'cart.giftcard.senders.name',
                value: giftCardSendersName,
              },
              {
                option_id: giftCardRecipientsEmailOptionId,
                option_value: giftCardRecipientsEmail,
                name: 'cart.giftcard.recipients.email',
                value: giftCardRecipientsEmail,
              },
              {
                option_id: giftCardDeliveryDateOptionId,
                option_value: Math.floor(
                  new Date(giftCardDeliveryDate).valueOf() / 1000 +
                    86400000 / 1000
                ),
                name: 'cart.giftcard.delivery.date',
                value: giftCardDeliveryDate,
              },
              {
                option_id: giftCardMessageOptionId,
                option_value: giftCardMessage,
                name: 'cart.giftcard.message',
                value: giftCardMessage,
              },
            ]
          : [
              {
                option_id: colourOptionId,
                option_value: `${selectedColour?.id}`,
                name: CART_COLOUR,
                value: `${selectedColour?.name}`,
              },
              ...(sizeOptionId
                ? [
                    {
                      option_id: sizeOptionId,
                      option_value: `${selectedSize?.id}`,
                      name: CART_SIZE,
                      value: `${selectedSize?.name}`,
                    },
                  ]
                : []),
            ],
      });
      if (isSaveForLater) {
        setAddingToCart(false);
        setCartDoneUpdating();
        setAddToCartClicked(false);
        setCartDoneUpdating();
      }
      if (!isSaveForLater) {
        setAddingToCart(false);
        openCartFlyout();
        setAddToCartClicked(false);
      }
      if (price && gaName && erpCategory && breadcrumbs && productCode) {
        const dataLayerProductData = {
          brand: brand ? { name: brand.name } : null,
          gaName,
          erpCategory,
          breadcrumbs,
          price: selectedVariant.price,
          entityId,
          selectedVariantSku: selectedVariant.sku,
          description,
          productCode,
          quantity,
          selectedColour,
          selectedSize,
          priceType: selectedVariant.price?.priceType,
          gender: getGenderFromString(gaName),
          inventoryAggregated:
            'inventoryAggregated' in selectedVariant
              ? selectedVariant.inventoryAggregated
              : null,
          sticker: promoBadge?.code,
          isCanadianBrand,
          madeInCanada,
          isMarketplacer: selectedVariant?.isMarketplace,
          isDropShip: selectedVariant?.isDropShip,
          hasComparison,
          hasMemberPriceDifference:
            hasMemberPriceDifference || promoBadge?.is_member_price,
          productTags,
        };

        const variantDataForAlgolia = {
          sku: saveForLaterItemToAddToCart?.sku || selectedVariant.sku || '',
          price:
            saveForLaterItemToAddToCart?.price || selectedVariant.price || null,
        };

        if (isSaveForLater) {
          trackAlgoliaAddToCart({ variantDataForAlgolia, quantity, locale });
          dataLayerSaveForLaterAddToCart(
            dataLayerProductData,
            productCode,
            quantity
          );
        } else {
          trackAlgoliaAddToCart({ variantDataForAlgolia, quantity, locale });
          trackAddToCartEvent({
            eventName: ADD_TO_CART,
            item: dataLayerProductData,
            extraAttributes: { cart_type: MINI_CART },
          });
          if (locale === 'en' && FEATURE_FLAG_ALBY_CHATBOT) {
            albyTrackAddToCart(
              selectedVariant.sku,
              selectedVariant.price?.price,
              quantity
            );
          }
        }
      }
    } catch (err) {
      setAddingToCart(false);
      if (isSaveForLater) setCartDoneUpdating();
    } finally {
      onAddToCart();
      if (saveForLaterItemToAddToCart?.selectedVariant?.entityId)
        removeSaveForLaterItem(
          saveForLaterItemToAddToCart?.selectedVariant?.entityId
        );
    }
  };

  return (
    <Button
      handleClick={onAddToCartClick}
      type="button"
      label={
        !isSaveForLater && addingToCart ? (
          <LoadingSpinner
            classNames={styles.loadingSpinner}
            colour="var(--color-white)"
          />
        ) : (
          <div
            className={cn(styles.buttonContent, {
              [styles.saveForLater]: isSaveForLater,
            })}
          >
            {!changeDeliveryMethodToAddress && (
              <IconWrapper
                icon={isSaveForLater ? `cart-outlined` : `cart`}
                fill="var(--color-white)"
                classNames={cn(styles.icon, {
                  [styles.saveForLater]: isSaveForLater,
                })}
                width={19}
              />
            )}
            {(changeDeliveryMethodToAddress &&
              t('product.ship.all.items.to.address')) ||
              t(
                isSaveForLater
                  ? 'common:saveForLater.add.to.cart'
                  : 'product.add.to.cart'
              )}
          </div>
        )
      }
      variant={buttonVariant}
      size="large"
      disabled={
        availabilityStatus === AvailabilityStatus.unavailable ||
        (addToCartClicked && !selectedVariant) ||
        addingToCart ||
        (!isGiftCard && !selectedAvailability)
      }
      className={cn(styles.button, {
        [styles.saveForLater]: isSaveForLater,
      })}
    />
  );
};

export default ProductAddToCartButton;
